/**
 * AutoSyncBadge — surfaces the BE auto-sync state for a drive connection.
 *
 * V1 reference: ``ando-frontend/app/components/drive/ProviderCard.tsx``
 * lines 394–427 (3-state badge + interval formatting + "Next sync at" /
 * "Last sync X ago"). V2 audit G1.
 *
 * Three states (all token-coloured, semantic palette):
 *
 *   1. ``enabled``    — green badge: "Auto-sync ogni HH:MM · prossima alle HH:MM"
 *   2. ``eligible``   — yellow badge: "Auto-sync in pausa"
 *   3. ``idle``       — gray badge: "Sincronizza per attivare l'auto-sync"
 *
 * Pure presentation — owns no fetch state. The caller passes the four
 * BE-side fields from ``DriveSyncStatus`` verbatim; the component branches
 * on them and renders. This keeps it trivially testable + reusable for
 * future per-row mounts (e.g. inside ``DocSyncManager``).
 */

import { Calendar, FileText, Pause, RefreshCw } from "lucide-react";
import type * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { cn } from "@ando/ui";

export interface AutoSyncBadgeProps {
  readonly autoSyncEnabled: boolean | undefined;
  readonly autoSyncEligible: boolean | undefined;
  readonly autoSyncIntervalMinutes: number | undefined;
  readonly nextAutoSyncAt: string | null | undefined;
  /**
   * Real indexed file count from ``content_dedup_index`` (V1 audit G7).
   * When present + > 0, a neutral "N file" chip is rendered BEFORE the
   * auto-sync badge so the row reads "5 file · Auto-sync · ogni 24:00".
   * Hidden when zero / undefined to keep the empty-state UX clean.
   */
  readonly filesIndexed?: number;
  /** Optional className for parent layout fine-tuning. */
  readonly className?: string;
}

/**
 * Format an interval in minutes as ``H:MM`` (V1 ``formatMinutesAsHMM``).
 * 1440 → "24:00", 90 → "1:30", 30 → "0:30". Below 1m falls back to "0:00".
 */
export function formatMinutesAsHMM(minutes: number | undefined | null): string {
  if (!minutes || minutes <= 0) return "0:00";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${String(mins).padStart(2, "0")}`;
}

/**
 * Format ``nextAutoSyncAt`` (ISO string) into an Italian-friendly
 * "prossima alle HH:MM" / "domani alle HH:MM" / "DD/MM alle HH:MM".
 * Returns an empty string when missing or unparseable so callers can
 * skip rendering without a defensive ternary.
 */
export function formatNextSyncAt(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const d = new Date(t);
  const now = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const time = `${hh}:${mm}`;
  // Same calendar day → no date prefix.
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `prossima alle ${time}`;
  // Tomorrow → "domani alle HH:MM".
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate()
  ) {
    return `domani alle ${time}`;
  }
  // Further away → "DD/MM alle HH:MM" (same calendar year) or
  // "DD/MM/YYYY alle HH:MM" (different year). Locking the year on cross-year
  // dates prevents "15/06" reading the same in 2026 and 2027.
  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  if (d.getFullYear() !== now.getFullYear()) {
    return `${dd}/${mo}/${d.getFullYear()} alle ${time}`;
  }
  return `${dd}/${mo} alle ${time}`;
}

const BADGE_BASE_CLASS =
  "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10.5px] font-medium";

// ``FilesIndexedChip`` — neutral counter shown next to the auto-sync
// badge when the BE has confirmed at least one file landed in the index.
// Italian thousands separator via ``toLocaleString`` so 12.345 reads as
// "12.345 file" (FE-wide convention; matches `formatRelativeTime`).
const FilesIndexedChip: React.FC<{ readonly count: number }> = ({ count }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-medium text-[10.5px]",
      "bg-ink-1/[0.04] text-ink-2",
    )}
    aria-label={`${count} file indicizzati`}
  >
    <FileText className="h-2.5 w-2.5" aria-hidden />
    <span>{count.toLocaleString("it-IT")} file</span>
  </span>
);

export const AutoSyncBadge: React.FC<AutoSyncBadgeProps> = ({
  autoSyncEnabled,
  autoSyncEligible,
  autoSyncIntervalMinutes,
  nextAutoSyncAt,
  filesIndexed,
  className,
}) => {
  const { i18n } = useLingui();
  // The counter chip rides ahead of the badge for every state — same
  // information should appear regardless of whether auto-sync is on.
  const indexedChip =
    typeof filesIndexed === "number" && filesIndexed > 0 ? (
      <FilesIndexedChip count={filesIndexed} />
    ) : null;
  const renderBadge = (): React.ReactElement => {
    if (autoSyncEnabled) {
      const interval = formatMinutesAsHMM(autoSyncIntervalMinutes);
      const next = formatNextSyncAt(nextAutoSyncAt);
      return (
        <span
          className={cn(BADGE_BASE_CLASS, "bg-success/15 text-success", className)}
          role="status"
          aria-label={
            next
              ? `Auto-sync attivo ogni ${interval}, ${next}`
              : `Auto-sync attivo ogni ${interval}`
          }
        >
          <RefreshCw className="h-2.5 w-2.5" aria-hidden />
          <span>Auto-sync · ogni {interval}</span>
          {next ? (
            <>
              <span aria-hidden className="text-success/70">
                ·
              </span>
              <Calendar className="h-2.5 w-2.5" aria-hidden />
              <span>{next}</span>
            </>
          ) : null}
        </span>
      );
    }
    if (autoSyncEligible) {
      return (
        <span
          className={cn(BADGE_BASE_CLASS, "bg-warning/15 text-warning", className)}
          role="status"
          aria-label="Auto-sync in pausa"
        >
          <Pause className="h-2.5 w-2.5" aria-hidden />
          <span>Auto-sync in pausa</span>
        </span>
      );
    }
    // Idle / never-synced — invite the user to do a first sync.
    return (
      <span
        className={cn(BADGE_BASE_CLASS, "bg-ink-3/10 text-ink-3", className)}
        role="status"
        aria-label={i18n._(msg`Sincronizza per attivare l'auto-sync`)}
      >
        <Calendar className="h-2.5 w-2.5" aria-hidden />
        <span>Sincronizza per attivare</span>
      </span>
    );
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      {indexedChip}
      {renderBadge()}
    </span>
  );
};

AutoSyncBadge.displayName = "AutoSyncBadge";
